const API_URL = '/api';

function getToken() {
  return localStorage.getItem('token');
}

function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  // SPA ohne Routing: Reload → App findet kein Token → Login-Ansicht
  window.location.reload();
}

// Refresh-Aufrufe entdoppeln: parallele 401er teilen sich denselben Refresh.
let refreshing: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = localStorage.getItem('refreshToken');
  if (!rt || rt === 'undefined') return false;
  try {
    const r = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!r.ok) return false;
    const d = await r.json();
    if (!d.accessToken) return false;
    localStorage.setItem('token', d.accessToken);
    if (d.refreshToken) localStorage.setItem('refreshToken', d.refreshToken);
    return true;
  } catch {
    return false;
  }
}

function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = doRefresh();
    refreshing.finally(() => { refreshing = null; });
  }
  return refreshing;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const isAuthPath = path.startsWith('/auth/');
  const doFetch = () =>
    fetch(`${API_URL}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res = await doFetch();

  // Access-Token abgelaufen → einmal automatisch erneuern und wiederholen
  if (res.status === 401 && !isAuthPath) {
    const ok = await tryRefresh();
    if (ok) {
      res = await doFetch();
    } else {
      forceLogout();
      throw new Error('Sitzung abgelaufen – bitte erneut anmelden.');
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Netzwerkfehler' }));
    throw new Error(err.error || 'Anfrage fehlgeschlagen');
  }
  return res.json();
}

export const api = {
  projects: {
    list: () => request<any[]>('GET', '/projects'),
    create: (data: { name: string; color?: string; description?: string }) =>
      request<any>('POST', '/projects', data),
    update: (id: string, data: { name?: string; color?: string }) =>
      request<any>('PATCH', `/projects/${id}`, data),
    delete: (id: string) => request<any>('DELETE', `/projects/${id}`),
  },
  folders: {
    list: (projectId: string) =>
      request<any[]>('GET', `/folders?projectId=${encodeURIComponent(projectId)}`),
    create: (projectId: string, name?: string) =>
      request<any>('POST', '/folders', { projectId, name }),
    delete: (id: string) => request<any>('DELETE', `/folders/${id}`),
  },
  worksheets: {
    list: (folderId: string) => request<any[]>('GET', `/worksheets/folder/${folderId}`),
    get: (id: string) => request<any>('GET', `/worksheets/${id}`),
    create: (data: { folderId: string; title: string; content?: unknown; settings?: unknown }) =>
      request<any>('POST', '/worksheets', data),
    update: (id: string, data: { title?: string; content?: unknown; settings?: unknown; isFavorite?: boolean }) =>
      request<any>('PATCH', `/worksheets/${id}`, data),
    delete: (id: string) => request<any>('DELETE', `/worksheets/${id}`),
  },
  notes: {
    list: () => request<any[]>('GET', '/notes'),
    create: (data: { title: string; content?: string; tags?: string[] }) =>
      request<any>('POST', '/notes', data),
    update: (id: string, data: { title?: string; content?: string; tags?: string[] }) =>
      request<any>('PATCH', `/notes/${id}`, data),
    delete: (id: string) => request<any>('DELETE', `/notes/${id}`),
  },
  todos: {
    list: () => request<any[]>('GET', '/todos'),
    create: (data: { title: string; priority?: string; dueDate?: string; category?: string }) =>
      request<any>('POST', '/todos', data),
    update: (id: string, data: { title?: string; priority?: string; isCompleted?: boolean }) =>
      request<any>('PATCH', `/todos/${id}`, data),
    delete: (id: string) => request<any>('DELETE', `/todos/${id}`),
  },
  ai: {
    generate: (data: { kind: string; topic: string; grade?: string }) =>
      request<{ html: string }>('POST', '/ai/generate', data),
  },
  images: {
    upload: (dataUrl: string) =>
      request<{ url: string; key: string }>('POST', '/images/upload', { dataUrl }),
  },
  calendar: {
    list: (start?: string, end?: string) =>
      request<any[]>('GET', `/calendar/events${start && end ? `?start=${start}&end=${end}` : ''}`),
    create: (data: unknown) => request<any>('POST', '/calendar/events', data),
    update: (id: string, data: unknown) => request<any>('PATCH', `/calendar/events/${id}`, data),
    delete: (id: string) => request<any>('DELETE', `/calendar/events/${id}`),
  },
};
